import { useState } from 'react'
import {
  validateAutomationUpdateCycles,
  validateNewAutomationCycles,
} from 'services/automations'
import { useAuth } from 'context/authentication/useAuth'

interface CycleValidationError {
  message: string
  automationIds?: number[]
}

export const useCycleValidation = () => {
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] =
    useState<CycleValidationError | null>(null)
  const { user } = useAuth()

  const validateNewAutomation = async (
    triggerType: string,
    actionType: string,
    formData?: {
      trigger_target_user_id?: number | null
      trigger_target_stage_id?: number | null
      target_user_id?: number | null
      target_stage_id?: number | null
      target_tag_id?: number | null
      target_campaign_id?: number | null
    }
  ): Promise<boolean> => {
    if (!user?.company?.id) {
      console.error('No company ID available for cycle validation')
      return true // Allow creation if we can't validate
    }

    setIsValidating(true)
    setValidationError(null)

    try {
      const result = await validateNewAutomationCycles(user.company.id, {
        trigger_type: triggerType,
        action_type: actionType,
        // Include target values for more precise cycle detection
        ...(formData?.trigger_target_user_id && {
          trigger_target_user_id: formData.trigger_target_user_id,
        }),
        ...(formData?.trigger_target_stage_id && {
          trigger_target_stage_id: formData.trigger_target_stage_id,
        }),
        ...(formData?.target_user_id && {
          target_user_id: formData.target_user_id,
        }),
        ...(formData?.target_stage_id && {
          target_stage_id: formData.target_stage_id,
        }),
        ...(formData?.target_tag_id && {
          target_tag_id: formData.target_tag_id,
        }),
        ...(formData?.target_campaign_id && {
          target_campaign_id: formData.target_campaign_id,
        }),
      })

      if (!result.valid) {
        setValidationError({
          message:
            result.message || 'This automation would create an infinite loop',
        })
        return false
      }

      return true
    } catch (error: any) {
      // Check if it's a validation error response
      if (error?.response?.data?.error) {
        setValidationError({
          message: error.response.data.error,
        })
        return false
      }

      // For other errors, allow creation (don't block on validation failure)
      return true
    } finally {
      setIsValidating(false)
    }
  }

  const validateUpdateAutomation = async (
    automationId: number,
    triggerType: string,
    actionType: string,
    formData?: {
      trigger_target_user_id?: number | null
      trigger_target_stage_id?: number | null
      target_user_id?: number | null
      target_stage_id?: number | null
      target_tag_id?: number | null
      target_campaign_id?: number | null
    }
  ): Promise<boolean> => {
    if (!user?.company?.id) {
      console.error('No company ID available for cycle validation')
      return true // Allow update if we can't validate
    }

    setIsValidating(true)
    setValidationError(null)

    try {
      const result = await validateAutomationUpdateCycles(
        user.company.id,
        automationId,
        {
          trigger_type: triggerType,
          action_type: actionType,
          // Include target values for more precise cycle detection
          ...(formData?.trigger_target_user_id && {
            trigger_target_user_id: formData.trigger_target_user_id,
          }),
          ...(formData?.trigger_target_stage_id && {
            trigger_target_stage_id: formData.trigger_target_stage_id,
          }),
          ...(formData?.target_user_id && {
            target_user_id: formData.target_user_id,
          }),
          ...(formData?.target_stage_id && {
            target_stage_id: formData.target_stage_id,
          }),
          ...(formData?.target_tag_id && {
            target_tag_id: formData.target_tag_id,
          }),
          ...(formData?.target_campaign_id && {
            target_campaign_id: formData.target_campaign_id,
          }),
        }
      )

      if (!result.valid) {
        setValidationError({
          message:
            result.message ||
            'This automation update would create an infinite loop',
        })
        return false
      }

      return true
    } catch (error: any) {
      // Check if it's a validation error response
      if (error?.response?.data?.error) {
        setValidationError({
          message: error.response.data.error,
        })
        return false
      }

      // For other errors, allow update (don't block on validation failure)
      return true
    } finally {
      setIsValidating(false)
    }
  }

  const clearValidationError = () => {
    setValidationError(null)
  }

  return {
    isValidating,
    validationError,
    validateNewAutomation,
    validateUpdateAutomation,
    clearValidationError,
  }
}
